import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, ArrowRight, Check } from "lucide-react";
import { hasRole, useAuth } from "@/lib/auth";
import SubdomainClaim from "@/components/SubdomainClaim";

const STORAGE_KEY = "axia.onboarding.dismissed";

export default function OnboardingWizard() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!hasRole(user, "tenant_admin")) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Only auto-open on the Home page so we don't intercept admins who arrive
    // with a clear destination (e.g. /admin/users, /cycles).
    if (location.pathname !== "/") return;
    api.get("/stats/home").then((r) => {
      setStats(r.data);
      setOpen(true);
    }).catch(() => setOpen(true));
  }, [user, location.pathname]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const STEPS = [
    {
      title: "Welcome to Axia",
      body: "Your performance home — goals, check-ins, reviews, calibration, AI assistance. Let's set up your workspace in 5 quick steps.",
      cta: "Get started", to: null,
    },
    {
      title: "1. Claim your branded URL",
      body: "Pick a subdomain so your team lives at your branded URL. Every kudos share, public review link and webhook callback uses it. You can change it once every 30 days.",
      cta: "Skip for now", to: null,
      render: <SubdomainClaim compact />,
    },
    {
      title: "2. Bring your team in",
      body: `Bulk-import your employee master (CSV) so org chart, lifecycle events, and history populate on day 1. We'll reject any compensation columns automatically. Currently: ${stats?.total_employees ?? 0} employees.`,
      cta: "Open bulk import", to: "/admin/import",
    },
    {
      title: "3. Pick a framework",
      body: "Three are pre-published: Balanced Scorecard, SMART + Values, and Classic OKR. Clone-to-edit so the original stays immutable.",
      cta: "Browse frameworks", to: "/frameworks",
    },
    {
      title: "4. Activate a cycle",
      body: "Activation auto-routes reviewers from your org chart and creates participant records for every active employee.",
      cta: "Open cycles", to: "/cycles",
    },
    {
      title: "5. You're set",
      body: "Employees can now draft objectives at /goals. Use AI features (SMART Coach, Bias Lens, Summary) inline. Track HR analytics at /analytics. We'll send notifications as people act.",
      cta: "Take a look around", to: "/",
    },
  ];

  const cur = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="max-w-md" data-testid="onboarding-wizard">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <span className="axia-overline">Onboarding · {step + 1}/{STEPS.length}</span>
          </div>
          <DialogTitle className="mt-2">{cur.title}</DialogTitle>
          <DialogDescription className="sr-only">{cur.body}</DialogDescription>
        </DialogHeader>
        <div className="text-sm text-slate-700">{cur.body}</div>
        {cur.render && <div className="mt-3">{cur.render}</div>}
        <div className="mt-1 flex items-center gap-1">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-slate-900" : "bg-slate-200"}`} data-testid={`onboarding-progress-${i}`} />
          ))}
        </div>
        <DialogFooter className="gap-2">
          {step > 0 && <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="onboarding-back">Back</Button>}
          {cur.to && (
            <Button variant="outline" asChild>
              <Link to={cur.to} onClick={dismiss} data-testid={`onboarding-open-${step}`}>
                {cur.cta} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Link>
            </Button>
          )}
          {!last ? (
            <Button onClick={() => setStep(step + 1)} data-testid={`onboarding-cta-${step}`}>
              Next <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={dismiss} data-testid="onboarding-finish"><Check className="w-3.5 h-3.5 mr-1.5" /> Finish</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
